// 화면 하단 슬라이드업 모달 — 공용
// Step 8 후속 #2 (2026-05-17) · AddAssetSheet/AddTransactionSheet 공용
// - backdrop 클릭 또는 ESC 키로 닫기
// - body 스크롤 잠금 (open 동안)
// - safe area inset 대응 (iOS 노치)

import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "./bottom-sheet.css";

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** footer는 sticky 하단 액션 영역 (취소/저장 버튼 등) */
  footer?: ReactNode;
  /** 시트별 레이아웃 변형. 공용 동작은 유지하고 형태만 구분한다. */
  variant?: "transaction" | "asset";
}

export function BottomSheet({ open, onClose, title, children, footer, variant }: BottomSheetProps) {
  // ESC 키 닫기 + body 스크롤 잠금
  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  // body로 portal — 호출부 DOM 위치에 상관없이 항상 최상위 레이어에 뜬다.
  // (portal 없이 화면 중간에서 렌더하면 조상의 stacking context에 갇혀 z-index:1000이 무력화 →
  //  2026-07-16 종목상세 차트설정 시트가 세그먼트·탭바에 가리던 버그. 전 시트 공통 예방.)
  // cs-root를 시트 루트에 부여하는 이유 (2026-07-19):
  //   --cs-accent 는 :root가 아니라 .cs-root 에서 oklch 합성된다(cs-tokens.css). 그런데 이 시트는
  //   body로 portal되어 .cs-root 바깥에 렌더되므로 accent가 '미정의'가 되어 var(--cs-accent)를 쓴
  //   버튼·토글이 투명해졌다(2026-07-16 portal 전환 때 유입된 버그. 실측: body에서 --cs-accent 빈 값).
  //   data-temp는 현재 화면의 cs-root에서 복사해 sand/mocha 테마까지 따라가게 한다.
  //   .bottom-sheet.cs-root { background: transparent } 로 cs-root의 --cs-bg 배경만 무효화(css 참조).
  const temp = document.querySelector(".cs-root")?.getAttribute("data-temp") ?? "warm";

  return createPortal(
    <div className="bottom-sheet cs-root" data-temp={temp} role="dialog" aria-modal="true" aria-label={title}>
      <div className="bottom-sheet__backdrop" onClick={onClose} aria-hidden="true" />
      <div className="bottom-sheet__panel" role="document" data-variant={variant}>
        <div className="bottom-sheet__handle" aria-hidden="true" />
        <div className="bottom-sheet__header">
          <h2 className="bottom-sheet__title">{title}</h2>
          <button
            type="button"
            className="bottom-sheet__close"
            onClick={onClose}
            aria-label="닫기"
          >
            {/* dc 정본은 ✕ 글자가 아니라 15px stroke svg — 글자는 폰트마다 두께·정렬이 흔들린다 */}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="bottom-sheet__body">{children}</div>
        {footer && <div className="bottom-sheet__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
